import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Trash, PencilSimple, X, CheckCircle, Palette } from "@phosphor-icons/react";
import HintButton from "@/components/HintButton";

const TYPES = [
  { key: "POP", label: "Pop Cup" },
  { key: "TWS", label: "Twister" },
  { key: "LNG", label: "Long Drink" },
  { key: "STA", label: "Stadium" },
  { key: "CAN", label: "Caneca" },
  { key: "DES", label: "Descartável" },
  { key: "ALL", label: "Todos (compartilhada)" },
];

const EMPTY = { name: "", type: "POP", hex: "", active: true };

export default function CupColors() {
  const [colors, setColors] = useState([]);
  const [activeTab, setActiveTab] = useState("POP");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");

  const load = async () => {
    const { data } = await api.get("/cup-colors/all");
    setColors(data);
  };
  useEffect(() => { load(); }, []);

  const byType = (t) => colors.filter((c) => c.type === t);
  const visible = activeTab === "ALL_VIEW" ? colors : byType(activeTab);

  const openNew = (t) => { setForm({ ...EMPTY, type: t }); setEditing("new"); setError(""); };
  const openEdit = (c) => { setForm({ name: c.name, type: c.type, hex: c.hex || "", active: c.active }); setEditing(c); setError(""); };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editing === "new") await api.post("/cup-colors", form);
      else await api.put(`/cup-colors/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro");
    }
  };

  const del = async (id) => {
    if (!confirm("Remover cor?")) return;
    await api.delete(`/cup-colors/${id}`);
    load();
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline text-zinc-500 mb-2">CADASTROS · COPOS</div>
          <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tight">Cores de Copos</h1>
          <HintButton text="Cadastre as cores disponíveis por tipo de copo. Aparecem como opções ao distribuir a quantidade nos itens do pedido." />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-zinc-200" data-testid="cup-color-tabs">
        {TYPES.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-3 text-xs uppercase tracking-widest font-bold border-b-2 ${activeTab === t.key ? "border-black text-black" : "border-transparent text-zinc-500 hover:text-black"}`}
            data-testid={`tab-${t.key}`}
          >
            {t.label} <span className="ml-1 font-mono text-zinc-400">({byType(t.key).length})</span>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="overline text-zinc-500">
          {TYPES.find((t) => t.key === activeTab)?.label} · {visible.length} {visible.length === 1 ? "cor" : "cores"}
        </div>
        <button className="btn" onClick={() => openNew(activeTab)} data-testid="add-cup-color">
          <Plus size={14} weight="bold" /> Nova Cor
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="cup-colors-grid">
        {visible.length === 0 && (
          <div className="col-span-full text-center py-10 border border-dashed border-zinc-300">
            <Palette size={32} weight="thin" className="mx-auto mb-2 text-zinc-300" />
            <div className="text-zinc-500 text-sm">Sem cores nesse tipo. Clique em "Nova Cor".</div>
          </div>
        )}
        {visible.map((c) => (
          <div key={c.id} className="border border-black bg-white p-4 flex items-center justify-between" data-testid={`cup-color-${c.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border border-black" style={{ background: c.hex || "#f4f4f5" }}>
                {!c.hex && <Palette size={18} className="w-full h-full p-2 text-zinc-400" />}
              </div>
              <div>
                <div className="font-semibold text-sm">{c.name}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{c.type}</div>
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => openEdit(c)} className="p-2 hover:bg-zinc-100" data-testid={`edit-cup-${c.id}`}><PencilSimple size={14} /></button>
              <button onClick={() => del(c.id)} className="p-2 hover:bg-red-50 text-red-500" data-testid={`del-cup-${c.id}`}><Trash size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <div className="glass max-w-md w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="cup-color-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline text-zinc-500">{editing === "new" ? "NOVA COR" : "EDITAR COR"}</div>
                <h2 className="font-head text-2xl font-bold">{form.name || "—"}</h2>
              </div>
              <button onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <div>
                <label className="label">Nome *</label>
                <input className="input" required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cup-color-name" />
              </div>
              <div>
                <label className="label">Tipo *</label>
                <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="cup-color-type">
                  {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Cor (HEX, opcional)</label>
                <div className="flex gap-2 items-center">
                  <input type="color" className="w-14 h-12 border border-zinc-300 cursor-pointer" value={form.hex || "#ffffff"} onChange={(e) => setForm({ ...form, hex: e.target.value })} />
                  <input className="input font-mono" value={form.hex || ""} placeholder="#ffffff" onChange={(e) => setForm({ ...form, hex: e.target.value })} data-testid="cup-color-hex" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Ativa
              </label>
              {error && <div className="border border-[#E60000] bg-red-50 text-[#E60000] text-sm px-3 py-2">{error}</div>}
              <div className="flex gap-3 pt-2">
                <button type="button" className="btn btn-ghost flex-1" onClick={() => setEditing(null)}>Cancelar</button>
                <button type="submit" className="btn flex-1" data-testid="cup-color-save"><CheckCircle size={14} weight="bold" /> Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
